/** The course workspace's loading and unavailable frames, composed by `CourseWorkspace`. */
import type { Ref } from "react";

import { PageRefusal } from "@/components/ui/box";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

/** The course workspace's shape while it loads: the frame, then the tiles. */
export function CourseHomeLoading() {
  return (
    <div aria-busy="true" aria-label="Loading course home" className="mx-auto flex w-full max-w-[90rem] flex-col gap-[var(--layout-bento-gap)] px-4 pt-4 pb-16 sm:px-7 sm:pt-6">
      <div className="grid gap-2">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-9 w-2/3" />
        <Skeleton className="h-5 w-1/2" />
      </div>
      <div className="grid gap-[var(--layout-bento-gap)] xl:grid-cols-12">
        <Skeleton className="h-40 rounded-xl xl:col-span-8" />
        <Skeleton className="h-40 rounded-xl xl:col-span-4" />
      </div>
    </div>
  );
}

export function CourseHomeUnavailable({
  noProjection,
  onRetry,
  backHref,
  headingRef,
}: {
  noProjection: boolean;
  onRetry: () => void;
  /** The seat's own courses: a refusal's one way back (R1). */
  backHref: string;
  headingRef?: Ref<HTMLHeadingElement>;
}) {
  // No course relationship is the kit's refusal: one sentence that names nothing
  // hidden, focused on arrival, and the seat's own courses as its way back (R1).
  if (noProjection)
    return (
      <div className="mx-auto w-full max-w-[90rem] px-4 sm:px-7">
        <PageRefusal
          id="course-unavailable"
          journey="course-home"
          refusal={{ reason: "forbidden", sentence: "You can't open this course.", back: { label: "Back to your courses", to: backHref } }}
        />
      </div>
    );
  // A failed read is not a refusal: it keeps Try again.
  return (
    <section
      className="mx-auto flex w-full max-w-[90rem] flex-col gap-[var(--layout-bento-gap)] px-4 pt-4 pb-16 sm:px-7 sm:pt-6"
      aria-labelledby="course-home-unavailable-heading"
    >
      <header data-slot="page-frame" className="grid min-w-0 items-end gap-5 lg:grid-cols-[minmax(0,1fr)_auto]">
        <div className="grid min-w-0 gap-2">
          <h1
            ref={headingRef}
            id="course-home-unavailable-heading"
            tabIndex={-1}
            className="-mx-1.5 rounded-control px-1.5 text-title font-semibold text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Course home unavailable
          </h1>
          <p className="text-ui text-muted-foreground">
            The course home could not be loaded. No course details have been shown.
          </p>
        </div>
        <Button type="button" variant="outline" onClick={onRetry}>Try again</Button>
      </header>
    </section>
  );
}
